import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppointmentSlot } from '@utils/api/aboboApiTypes';
import AppointmentPicker from '@/components/common/AppointmentPicker';
import { useGuidedBookingContext } from '@contexts/GuidedBookingContext';
import { useBranding, useClient } from '@utils/api';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';

const AppointmentSelectionStep: FC = () => {
  const { branding } = useBranding();
  const { client } = useClient();
  const { t } = useTranslation();
  const { bookingData, updateBookingData, goForward, tokenData } = useGuidedBookingContext();

  // Timezone calculation
  const timeZoneDiff = useMemo(() => {
    if (!client) return 0;
    const date = new Date();
    const offset = date.getTimezoneOffset() / -60;
    return offset - client.timeZoneOffset;
  }, [client]);

  const timeZoneMessage = () => {
    if (timeZoneDiff === 0) return '';
    if (timeZoneDiff < 0) return t('booking.timeZoneAhead', {
      count: Math.abs(timeZoneDiff), 
      clinicName: branding?.name
    });
    if (timeZoneDiff > 0) return t('booking.timeZoneBehind', {
      count: timeZoneDiff, 
      clinicName: branding?.name
    });
  };

  const handleSlotSelect = (slot: AppointmentSlot) => {
    updateBookingData({
      scheduledAt: slot.time,
      operatorId: slot.operatorIds[0],
    });
    goForward('bookingConfirmation');
  };

  return (
    <div className="pt-4 pb-2">
      <Separator aria-hidden="true" role="separator"/>
      
      {timeZoneDiff !== 0 && (
        <Alert className="flex flex-col items-center justify-center text-center pb-2 mb-4">
          <AlertTitle>{t('booking.timeZoneDiff')}</AlertTitle>
          <AlertDescription>
            <div>{timeZoneMessage()}</div>
            <div>{t('booking.timeZoneInfo')}</div>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <AppointmentPicker 
          serviceId={tokenData?.serviceId || ''} 
          operatorIds={tokenData?.operatorIds}
          startDate={tokenData?.startDate}
          endDate={tokenData?.endDate}
          onChange={handleSlotSelect}
        />
      </div>
    </div>
  );
};

export default AppointmentSelectionStep;